// Solomon Portal — typed fetch wrapper for the `list-conversations` Edge
// Function (issue #9 frontend half).
//
// Contract (from the backend agent, owner of supabase/functions/list-conversations):
//
//   GET /functions/v1/list-conversations
//     ?status=active|closed|all
//     &q=<text>
//     &modifiedSince=<ISO8601>
//     &page=<int 1-indexed>
//     &pageSize=<int, server-clamped 1..50>
//
//   200 OK
//     { items: ConversationListItem[], page, pageSize, total, hasMore }
//
//   401 Unauthorized   — no/expired JWT (frontend redirects to /login)
//   403 Forbidden      — caller deactivated / not provisioned
//   502 Bad Gateway    — Help Scout upstream failure
//   500 Internal       — Edge Function configuration error
//
// The Edge Function is the trust boundary: it forces customer.email = the
// authenticated user's email and bakes `tag:NOT(portal-hidden)` into the
// Mailbox API query regardless of caller-supplied params (see ADR-024 +
// ADR-025 + ADR-026).

import { supabase } from './supabase';
import { env } from './env';
import { logger } from './logger';

/**
 * Conversation status filter values exposed to the UI.
 *
 * Mapping to Help Scout statuses (ADR-037, live-verified 2026-05-13):
 *   - 'open'   → HS `status=open` (returns `active` + `pending` per HS
 *                semantics). This matches the user-facing "Open"
 *                mental model: every conversation that isn't closed,
 *                regardless of whether HS has it on an active queue
 *                or in a pending/paused state.
 *   - 'closed' → HS `status=closed`.
 *   - 'all'    → HS `status=all`.
 *
 * Historical: prior to ADR-037 the value was 'active', which mapped
 * 1:1 to HS `status=active` and silently excluded `pending` conversations
 * from the OPEN tab. The Edge Function accepts the legacy `active` value
 * as a backward-compat alias for `open` and rewrites it to `open` on
 * the way to HS (ADR-037 backward-compat shim).
 */
export type ConversationStatusFilter = 'all' | 'open' | 'closed';

/** Date-range filter values exposed to the UI; mapped to `modifiedSince`. */
export type DateRangeFilter = 'all' | '30d' | '90d';

/** Tag rendered on the list row. Server already strips `portal-hidden` + internal-prefixed tags. */
export interface ConversationListTag {
  id: number;
  color: string;
  tag: string;
}

/** A single row in the conversation list. Sanitized server-side. */
export interface ConversationListItem {
  id: number;
  number: number;
  subject: string;
  status: string;
  createdAt: string;
  modifiedAt: string;
  threadCount: number;
  primaryCustomer: {
    firstName: string | null;
    lastName: string | null;
  } | null;
  tags: ConversationListTag[];
}

export interface ListConversationsResponse {
  items: ConversationListItem[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface ListConversationsOpts {
  status: ConversationStatusFilter;
  /** Free-text search; empty string means no full-text filter. */
  q: string;
  dateRange: DateRangeFilter;
  /** 1-indexed page number. Server clamps to >= 1. */
  page: number;
  /** Server clamps to 1..50. We always request 25. */
  pageSize: number;
  /** AbortSignal for cancellation when the user changes filters mid-flight. */
  signal?: AbortSignal;
}

/** Outcome union — never throws. */
export type ListConversationsResult =
  | { kind: 'ok'; response: ListConversationsResponse }
  | { kind: 'unauthenticated' }
  | { kind: 'forbidden' }
  | { kind: 'upstream_unavailable' }
  | { kind: 'config_error' }
  | { kind: 'network_error' }
  | { kind: 'aborted' };

/**
 * Translate a UI date-range token into the `modifiedSince` ISO string.
 *
 * Help Scout's Mailbox API rejects timestamps that include a fractional
 * second (`.000Z`) with HTTP 400, even though `Date.toISOString()`
 * always emits one. We strip the fractional component here so the
 * Edge Function (and any other consumer) can pass the value straight
 * through. The Edge Function ALSO normalizes defensively — see
 * `normalizeModifiedSinceForHelpScout` in `supabase/functions/_shared/helpscout.ts`
 * (ADR-033).
 */
export function dateRangeToModifiedSince(
  range: DateRangeFilter,
  now: Date = new Date(),
): string | null {
  if (range === 'all') return null;
  const days = range === '30d' ? 30 : 90;
  const ms = now.getTime() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString().replace(/\.\d+Z$/, 'Z');
}

/**
 * Fetch one page of the conversation list. Returns a tagged-union outcome
 * so the UI can switch over `result.kind` instead of try/catching network
 * errors. The function never throws.
 */
export async function listConversations(
  opts: ListConversationsOpts,
): Promise<ListConversationsResult> {
  let session;
  try {
    const { data } = await supabase.auth.getSession();
    session = data.session;
  } catch (err: unknown) {
    logger.error({
      source: 'conversation-list-api.getSession',
      message: 'getSession threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }
  if (!session) return { kind: 'unauthenticated' };

  const url = new URL(`${env.VITE_SUPABASE_URL}/functions/v1/list-conversations`);
  url.searchParams.set('status', opts.status);
  if (opts.q.trim().length > 0) {
    url.searchParams.set('q', opts.q.trim());
  }
  const modifiedSince = dateRangeToModifiedSince(opts.dateRange);
  if (modifiedSince !== null) {
    url.searchParams.set('modifiedSince', modifiedSince);
  }
  url.searchParams.set('page', String(opts.page));
  url.searchParams.set('pageSize', String(opts.pageSize));

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Authorization: `Bearer ${session.access_token}` },
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  } catch (err: unknown) {
    if (opts.signal?.aborted) return { kind: 'aborted' };
    logger.error({
      source: 'conversation-list-api.fetch',
      message: 'fetch threw',
      payload: { error: String(err) },
    });
    return { kind: 'network_error' };
  }

  if (res.status === 401) return { kind: 'unauthenticated' };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 502) return { kind: 'upstream_unavailable' };
  if (res.status === 500) return { kind: 'config_error' };
  if (!res.ok) {
    logger.warn({
      source: 'conversation-list-api.fetch',
      message: 'unexpected non-OK status',
      payload: { status: res.status },
    });
    return { kind: 'upstream_unavailable' };
  }

  let body: ListConversationsResponse;
  try {
    body = (await res.json()) as ListConversationsResponse;
  } catch (err: unknown) {
    logger.error({
      source: 'conversation-list-api.fetch',
      message: 'response was not valid JSON',
      payload: { error: String(err) },
    });
    return { kind: 'upstream_unavailable' };
  }

  // Light shape-check so a buggy upstream doesn't crash the UI.
  if (
    !Array.isArray(body.items) ||
    typeof body.page !== 'number' ||
    typeof body.total !== 'number'
  ) {
    logger.error({
      source: 'conversation-list-api.fetch',
      message: 'response shape invalid',
      payload: { keys: Object.keys(body) },
    });
    return { kind: 'upstream_unavailable' };
  }

  return { kind: 'ok', response: body };
}

/** Default page size; matches the Edge Function default and the spec. */
export const DEFAULT_PAGE_SIZE = 25;
